import { ConfirmDialog } from "@/components/ui";
import { useT, type Translator } from "@/i18n";
import { framesHtmlMessages } from "@/i18n/messages/frames-html";
import { findToolkit } from "@/lib/composio";
import { useFramesStore } from "@/stores/frames-store";
import { useIntegrationStore } from "@/stores/integration-store";
import { useTasksStore } from "@/stores/tasks-store";
import type {
  HtmlFrameActionHost,
  HtmlFrameHostAction,
} from "./html-frame-actions";
import { pendingHtmlFrameQuestion } from "./html-frame-action-dispatch";
import type { HtmlFrameActionConfirmation } from "./html-frame-capabilities";

type Messages = Translator<typeof framesHtmlMessages.en>;

/** The click that opened the dialog may be half of a double-click. */
const CONFIRM_ARM_MS = 400;

function taskTitle(taskId: string): string | undefined {
  return useTasksStore.getState().tasks.find((task) => task.id === taskId)
    ?.title;
}

/** The question, in the app's own words: every name comes from app stores. */
function actionTitle(
  t: Messages,
  host: HtmlFrameActionHost,
  action: HtmlFrameHostAction,
): string {
  switch (action.name) {
    case "run_task": {
      const task = taskTitle(action.args.taskId);
      return task ? t("actionRunNamed", { task }) : t("actionRunUnnamed");
    }
    case "open_task": {
      const task = taskTitle(action.args.taskId);
      return task
        ? t("actionOpenTaskNamed", { task })
        : t("actionOpenTaskUnnamed");
    }
    case "open_frame": {
      const title =
        useFramesStore.getState().framesById[action.args.frameId]?.title;
      return title
        ? t("actionOpenFrameNamed", { title })
        : t("actionOpenFrameUnnamed");
    }
    case "open_connect": {
      const toolkit = findToolkit(
        action.args.toolkit,
        useIntegrationStore.getState().toolkits,
      );
      return t("actionOpenConnect", {
        app: toolkit?.display_name || action.args.toolkit,
      });
    }
    case "answer_question": {
      const pending = pendingHtmlFrameQuestion(
        host.frameId,
        action.args.questionId,
        action.args.optionId,
      );
      return t("actionAnswer", { option: pending?.option.label ?? "" });
    }
  }
}

/** What happens on Confirm, in the app's own words. */
function actionDescription(t: Messages, action: HtmlFrameHostAction): string {
  switch (action.name) {
    case "run_task":
      return t("actionRunDescription");
    default:
      return t("actionFromPageDescription");
  }
}

export function HtmlFrameActionConfirm({
  confirmation,
}: {
  confirmation: HtmlFrameActionConfirmation;
}) {
  const t = useT(framesHtmlMessages);
  const { host, action } = confirmation;
  const run = action.name === "run_task";
  // An answer shows the question as Denker asked it, never the page's text.
  const question =
    action.name === "answer_question"
      ? pendingHtmlFrameQuestion(
          host.frameId,
          action.args.questionId,
          action.args.optionId,
        )?.question.question
      : undefined;
  return (
    <ConfirmDialog
      title={actionTitle(t, host, action)}
      description={question || actionDescription(t, action)}
      confirmLabel={run ? t("actionRunConfirm") : undefined}
      variant="default"
      armDelayMs={CONFIRM_ARM_MS}
      onConfirm={confirmation.confirm}
      onCancel={confirmation.cancel}
      data-testid="html-frame-action-confirm"
    />
  );
}
